import type { AppServices } from '../app';
import { services } from '../app';
import { altAzToEnu, type Vec3 } from '../lib/math';
import type { FrameContext, SkyProvider } from '../render/engine';
import { FAMILY } from '../render/overlay';
import {
  endPreview, goLive, labelScale, panel, previewFrom, previewPlace, reliveFocus, reliving, selection, setSimTime, simNow, timeLive, timeVersion, viewMode,
} from '../state';
import { fmtTime } from '../ui/format';
import { keyToSelection, nights, sightingName } from './nights';
import { Night, Sighting, nightSpan } from './nightsCore';

// Reliving a saved night: the whole sky as it was, live. The app goes back to that date, time and place
// (home is kept, as for any preview), the clock stands still at the moment chosen, and the satellites seen
// then come back from what was saved with them. Pointing works as usual: facing south shows that night's
// southern sky above the place it was saved. Planes, which older nights may hold, are no longer shown.

/**
 * Start reliving a night: at a sighting (facing it), at a moment, or by default at its first sighting (or
 * the moment kept, facing the way the view looked then).
 */
export function startRelive(s: AppServices, n: Night, at?: number, focus?: Sighting) {
  const [a, b] = nightSpan(n);
  // At least an hour to move through, and a little either side of what happened.
  const mid = (a + b) / 2, half = Math.max(30 * 60e3, (b - a) / 2 + 20 * 60e3);
  const from = mid - half, to = mid + half;
  const target = focus ?? (at === undefined ? n.sightings[0] : undefined);
  const t = at ?? target?.t ?? n.kept?.t ?? a;
  // From one relived night straight to another: the watchers below must not end the new one on the way.
  reliving.value = null;
  selection.value = null;
  panel.value = null;
  previewPlace({ ...n.place }, n.place.name, 'relive');
  setSimTime(new Date(t), 0);
  // Only what was seen that night moves across it: its satellites, from their saved orbits.
  s.sats.relive(n.sightings.filter((x) => x.orbit).map((x) => x.orbit!), t);
  reliving.value = { night: n.id, from, to };
  if (target) face(s, target);
  else if (n.kept && t === n.kept.t) face(s, null, altAzToEnu(n.kept.at[0], n.kept.at[1]), n.kept.fov);
}

/** Move the relived night to another moment, and turn towards a sighting if one was chosen. */
export function reliveAt(s: AppServices, t: number, focus?: Sighting) {
  setSimTime(new Date(t), 0);
  if (focus) face(s, focus);
}

/**
 * Turn towards a sighting at its own moment: the view moves by touch; with the phone pointing, a guide says
 * which way. Where it was is saved with it (as seen then), which is right even before the sky has redrawn
 * for the new time; an old sighting without it is found on the sky once it has.
 */
function face(s: AppServices, x: Sighting | null, dir?: Vec3, fov?: number) {
  const sel = x ? keyToSelection(s, x.key) : null;
  if (viewMode.peek() === 'ar') {
    reliveFocus.value = sel;
    return;
  }
  reliveFocus.value = null;
  const enu = dir ?? (x?.at ? altAzToEnu(x.at[0], x.at[1]) : null);
  if (enu) s.engine.flyTo(enu, fov, 1.1, 0.12);
  else if (sel) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const l = s.engine.locate(sel);
      if (l) s.engine.flyTo(l.enu, undefined, 1.1, 0.12);
    }));
  }
}

/**
 * Stop reliving: today's satellites, home (unless the place was already changed) and the time now (unless
 * another time was chosen, which is kept).
 */
export function endRelive(s: AppServices | null = services.peek(), goHome = true, keepTime = false) {
  if (!reliving.peek()) return;
  reliving.value = null;
  reliveFocus.value = null;
  s?.sats.relive(null);
  if (goHome) endPreview();
  if (!keepTime) goLive();
}

// Going back to now by any route (the time's Now button) ends reliving too. So does another preview (a dark
// site) or a new home place: those set the place themselves, so reliving must not put home back over them.
timeLive.subscribe((live) => live && reliving.peek() && endRelive());
previewFrom.subscribe((p) => (!p || p.kind !== 'relive') && reliving.peek() && endRelive(undefined, false));
// Moving the time away from the night (the Time panel's days, say) leaves it, at the time chosen.
timeVersion.subscribe(() => {
  const r = reliving.peek();
  const t = simNow().getTime();
  if (r && (t < r.from - 3600e3 || t > r.to + 3600e3)) endRelive(undefined, true, true);
});
// A relived night that is deleted (or loses its last sighting) ends it.
nights.subscribe((ns) => {
  const r = reliving.peek();
  if (r && !ns[r.night]) endRelive();
});

// ---------- On the sky: each thing seen that night, ringed in gold ----------
const GOLD = 'rgba(239, 207, 143, 0.92)';

/**
 * A name beside a point: to the right, or to the left where it would run off the screen, moved down a line
 * where it would cover a name already placed (and left out if there is no room).
 */
function tag(g: CanvasRenderingContext2D, text: string, x: number, y: number, off: number, width: number, placed: number[][], below = false, top = 118) {
  const w = g.measureText(text).width, h = 17 * labelScale.peek();
  const right = x + off + w < width - 8;
  // Under the ring, centred (for things the sky already names to one side), and always on screen, clear of
  // the bars at the top.
  const x0 = Math.max(8, Math.min(width - 8 - w, below ? x - w / 2 : right ? x + off : x - off - w));
  if (below) y += off;
  y = Math.max(y, top);
  for (let k = 0; k < 3; k++, y += h) {
    if (placed.some(([a, b, c, d]) => x0 < c && x0 + w > a && y - h / 2 < d && y + h / 2 > b)) continue;
    placed.push([x0, y - h / 2, x0 + w, y + h / 2]);
    g.fillText(text, x0, y);
    return;
  }
}

/** Draws the relived night's sightings where they are at the time on show (shooting stars near their moment). */
export class ReliveMarks implements SkyProvider {
  update(_ctx: FrameContext) {}

  overlay(g: CanvasRenderingContext2D, ctx: FrameContext) {
    const r = reliving.peek();
    const s = services.peek();
    const n = r && nights.peek()[r.night];
    if (!n || !s) return;
    const cam = ctx.camera;
    const now = ctx.date.getTime();
    const sel = selection.peek();
    g.save();
    g.font = `600 ${13 * labelScale.peek()}px ${FAMILY}`;
    g.textBaseline = 'middle';
    g.textAlign = 'left';
    g.shadowColor = 'rgba(0,0,0,0.75)';
    const placed: number[][] = [];
    // Clear of the top bar and the banner under it (below the status bar on a phone).
    const top = s.engine.insets.top + 104;
    for (const x of n.sightings) {
      // The Moon, planets and satellites are named on the sky already: under their rings, just the time.
      const named = x.kind === 'body' || x.kind === 'satellite';
      const label = named ? fmtTime(x.t) : `${sightingName(s, x)} · ${fmtTime(x.t)}`;
      if (x.kind === 'meteor') {
        // A shooting star lasts a moment: its path shows for ten minutes either side of when it was seen.
        const age = Math.abs(now - x.t);
        if (!x.path || age > 10 * 60e3) continue;
        const a = 1 - age / (10 * 60e3) * 0.6;
        const pts = x.path.map(([alt, az]) => cam.project(altAzToEnu(alt, az), { x: 0, y: 0 })).filter((p): p is { x: number; y: number } => !!p);
        if (pts.length < 2) continue;
        g.strokeStyle = `rgba(239, 207, 143, ${0.85 * a})`;
        g.lineWidth = 2.5;
        g.lineCap = 'round';
        g.beginPath();
        pts.forEach((p, k) => (k ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
        g.stroke();
        const end = pts[pts.length - 1];
        g.fillStyle = `rgba(239, 207, 143, ${a})`;
        g.shadowBlur = 6;
        tag(g, label, end.x, end.y, 10, cam.width, placed, false, top);
        g.shadowBlur = 0;
        continue;
      }
      const xs = keyToSelection(s, x.key);
      if (!xs || (sel && JSON.stringify(sel) === JSON.stringify(xs))) continue;
      const loc = s.engine.locate(xs);
      if (!loc || loc.enu[2] < -0.02) continue;
      const p = cam.project(loc.enu, { x: 0, y: 0 });
      if (!p || !cam.onScreen(p, 40)) continue;
      g.strokeStyle = GOLD;
      g.lineWidth = 1.6;
      g.beginPath();
      g.arc(p.x, p.y, 17, 0, Math.PI * 2);
      g.stroke();
      g.fillStyle = GOLD;
      g.shadowBlur = 6;
      tag(g, label, p.x, p.y, 30, cam.width, placed, true, top);
      g.shadowBlur = 0;
    }
    g.restore();
  }
}
